import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/design-system/primitives/Toast/Toast';
import {
  approveInvoice,
  approveMeerwerk,
  attestKeuring,
  type BatchCreateTasksCommand,
  batchCreateTasks,
  type CreateContactCommand,
  type CreateDecisionCommand,
  type CreateInvoiceCommand,
  type CreateKeuringCommand,
  type CreateMeerwerkCommand,
  type CreateMilestoneCommand,
  type CreatePlanCommand,
  type CreateQuoteCommand,
  type CreateTaskCommand,
  type CreateWerfverslagCommand,
  chooseDecision,
  clarifyInvoice,
  createContact,
  createDecision,
  createInvoice,
  createKeuring,
  createMeerwerk,
  createMilestone,
  createPlan,
  createQuote,
  createShareLink,
  createTask,
  createWerfverslag,
  deleteAccount,
  deleteContact,
  deleteDecision,
  deleteDocument,
  deleteInvoice,
  deletePhoto,
  deletePlan,
  deleteQuote,
  deleteTask,
  downloadDocument,
  downloadDocumentsZip,
  downloadPhoto,
  // BF-8 — plans multipart upload + download (BF-1 backend).
  downloadPlan,
  exportAccount,
  // F13 — search + activity export.
  exportActivityCsv,
  exportBudgetCsv,
  exportContactsVCard,
  exportMeerwerkenCsv,
  getBudget,
  getBudgetEstimates,
  getDashboard,
  // BF-11 / BF-12 — settings read endpoints (BF-5) + project read (BF-11).
  getNotifications,
  getPhase,
  getPhotoThumbnail,
  getProject,
  // F12 — settings mutations.
  type InviteUserCommand,
  inviteUser,
  type ListContactsData,
  type ListDecisionsData,
  type ListDocumentsData,
  type ListInvoicesData,
  type ListMeerwerkenData,
  // BF-10 — milestones read.
  type ListMilestonesData,
  type ListPhotosData,
  type ListPlansData,
  type ListQuotesData,
  type ListTasksData,
  type ListWerfverslagenData,
  listActivity,
  listContacts,
  listDecisions,
  listDocuments,
  listGaranties,
  listInvoices,
  listKeuringen,
  listMeerwerken,
  listMilestones,
  listPhases,
  listPhotos,
  listPlans,
  listProjectMembers,
  listQuotes,
  listShareLinks,
  listTasks,
  listWerfverslagen,
  mailPhaseContacts,
  objectInvoice,
  type PhaseId,
  type RotatePhotoCommand,
  rejectMeerwerk,
  rotatePhoto,
  type SearchResultItem,
  type SendWerfverslagResult,
  type SetBudgetEstimatesCommand,
  search,
  seedStandardKeuringen,
  selectQuote,
  sendWerfverslag,
  setBudgetEstimates,
  setTaskStatus,
  setTaskUrgent,
  type TaskStatus,
  type UpdateContactCommand,
  type UpdateDecisionCommand,
  type UpdateDocumentCommand,
  type UpdateInvoiceCommand,
  type UpdateKeuringCommand,
  type UpdateMeerwerkCommand,
  type UpdateNotificationsCommand,
  type UpdatePhaseCommand,
  type UpdatePhotoCommand,
  type UpdatePlanCommand,
  type UpdateProjectCommand,
  type UpdateQuoteCommand,
  type UpdateTaskCommand,
  type UpdateWerfverslagCommand,
  updateContact,
  updateDecision,
  updateDocument,
  updateInvoice,
  updateKeuring,
  updateMeerwerk,
  updateNotifications,
  updatePhase,
  updatePhoto,
  updatePlan,
  updateProject,
  updateQuote,
  updateTask,
  updateWerfverslag,
  uploadDocument,
  uploadDocumentVersion,
  uploadPhoto,
  uploadPlan,
  uploadPlanVersion,
} from './generated';

import './client';

// ---------- query keys ----------

export const keys = {
  dashboard: () => ['dashboard'] as const,
  phases: () => ['phases'] as const,
  phase: (id: string) => ['phases', id] as const,
  budget: () => ['budget'] as const,
  budgetEstimates: () => ['budget', 'estimates'] as const,
  documents: (q: ListDocumentsData['query']) => ['documents', q ?? {}] as const,
  contacts: (q: ListContactsData['query']) => ['contacts', q ?? {}] as const,
  invoices: (q: ListInvoicesData['query']) => ['invoices', q ?? {}] as const,
  decisions: (q: ListDecisionsData['query']) => ['decisions', q ?? {}] as const,
  meerwerken: (q: ListMeerwerkenData['query']) => ['meerwerken', q ?? {}] as const,
  photos: (q: ListPhotosData['query']) => ['photos', q ?? {}] as const,
  werfverslagen: (q: ListWerfverslagenData['query']) => ['werfverslagen', q ?? {}] as const,
  keuringen: () => ['keuringen'] as const,
  garanties: () => ['garanties'] as const,
  activity: (page: number) => ['activity', page] as const,
  tasks: (q: ListTasksData['query']) => ['tasks', q ?? {}] as const,
  quotes: (q: ListQuotesData['query']) => ['quotes', q ?? {}] as const,
  plans: (q: ListPlansData['query']) => ['plans', q ?? {}] as const,
  // BF-11 / BF-12 — settings + project reads.
  project: () => ['project'] as const,
  notifications: () => ['settings', 'notifications'] as const,
  shareLinks: () => ['settings', 'share-links'] as const,
  members: () => ['settings', 'members'] as const,
  // BF-10 — milestones read; key includes the phase filter so the Tijdlijn
  // (no filter) and PhaseDetail (filter=phase) keep separate caches.
  milestones: (q?: ListMilestonesData['query']) => ['milestones', q ?? {}] as const,
};

// ---------- unwrap helper ----------

// Generated SDK calls return `Promise<{ data, error, response }>`. We always
// want `data` or to throw on error so TanStack Query can surface the failure.
async function unwrap<T>(promise: Promise<{ data?: T; error?: unknown }>): Promise<T> {
  const result = await promise;
  if (result.error !== undefined) {
    throw result.error;
  }
  if (result.data === undefined) {
    throw new Error('Empty response from API.');
  }
  return result.data;
}

// ---------- hooks ----------

export function useDashboard() {
  return useQuery({
    queryKey: keys.dashboard(),
    queryFn: () => unwrap(getDashboard()),
  });
}

export function usePhases() {
  return useQuery({
    queryKey: keys.phases(),
    queryFn: () => unwrap(listPhases()),
  });
}

export function usePhase(phaseId: PhaseId | string) {
  return useQuery({
    queryKey: keys.phase(phaseId),
    queryFn: () => unwrap(getPhase({ path: { phaseId } })),
    enabled: phaseId.length > 0,
  });
}

export function useBudget() {
  return useQuery({
    queryKey: keys.budget(),
    queryFn: () => unwrap(getBudget()),
  });
}

// Per-post expected costs (offertewaarde). Drives the per-post percentage on
// the Budget page (besteed / verwacht).
export function useBudgetEstimates() {
  return useQuery({
    queryKey: keys.budgetEstimates(),
    queryFn: () => unwrap(getBudgetEstimates()),
  });
}

export function useDocuments(query?: ListDocumentsData['query']) {
  return useQuery({
    queryKey: keys.documents(query),
    queryFn: () => unwrap(listDocuments({ query })),
  });
}

export function useContacts(query?: ListContactsData['query']) {
  return useQuery({
    queryKey: keys.contacts(query),
    queryFn: () => unwrap(listContacts({ query })),
  });
}

export function useInvoices(query?: ListInvoicesData['query']) {
  return useQuery({
    queryKey: keys.invoices(query),
    queryFn: () => unwrap(listInvoices({ query })),
  });
}

export function useDecisions(query?: ListDecisionsData['query']) {
  return useQuery({
    queryKey: keys.decisions(query),
    queryFn: () => unwrap(listDecisions({ query })),
  });
}

export function useMeerwerken(query?: ListMeerwerkenData['query']) {
  return useQuery({
    queryKey: keys.meerwerken(query),
    queryFn: () => unwrap(listMeerwerken({ query })),
  });
}

export function usePhotos(query?: ListPhotosData['query']) {
  return useQuery({
    queryKey: keys.photos(query),
    queryFn: () => unwrap(listPhotos({ query })),
  });
}

export function useWerfverslagen(query?: ListWerfverslagenData['query']) {
  return useQuery({
    queryKey: keys.werfverslagen(query),
    queryFn: () => unwrap(listWerfverslagen({ query })),
  });
}

export function useKeuringen() {
  return useQuery({
    queryKey: keys.keuringen(),
    queryFn: () => unwrap(listKeuringen({ query: { pageSize: 50 } })),
  });
}

export function useGaranties() {
  return useQuery({
    queryKey: keys.garanties(),
    queryFn: () => unwrap(listGaranties({ query: { pageSize: 50 } })),
  });
}

export function useActivity(page = 1) {
  return useQuery({
    queryKey: keys.activity(page),
    queryFn: () => unwrap(listActivity({ query: { page, pageSize: 25 } })),
  });
}

export function useTasks(query?: ListTasksData['query']) {
  return useQuery({
    queryKey: keys.tasks(query),
    queryFn: () => unwrap(listTasks({ query })),
  });
}

export function useQuotes(query?: ListQuotesData['query']) {
  return useQuery({
    queryKey: keys.quotes(query),
    queryFn: () => unwrap(listQuotes({ query })),
  });
}

export function usePlans(query?: ListPlansData['query']) {
  return useQuery({
    queryKey: keys.plans(query),
    queryFn: () => unwrap(listPlans({ query })),
  });
}

// BF-11 — full project read. The Settings/Project tab and the onboarding
// wizard depend on this to round-trip dates / budget / phase / type that
// the dashboard DTO doesn't surface.
export function useProject() {
  return useQuery({
    queryKey: keys.project(),
    queryFn: () => unwrap(getProject()),
  });
}

// BF-12 — settings read endpoints (BF-5 backend). Each one mirrors the
// matching mutation hook so the SPA can hydrate from persisted state instead
// of fixtures and the toggle-then-reload-and-it-reverted bug goes away.
export function useNotifications() {
  return useQuery({
    queryKey: keys.notifications(),
    queryFn: () => unwrap(getNotifications()),
  });
}

export function useShareLinks() {
  return useQuery({
    queryKey: keys.shareLinks(),
    queryFn: () => unwrap(listShareLinks()),
  });
}

export function useMembers() {
  return useQuery({
    queryKey: keys.members(),
    queryFn: () => unwrap(listProjectMembers()),
  });
}

// BF-10 — milestones list. Pass `{ phaseId }` to scope to a single phase.
export function useMilestones(query?: ListMilestonesData['query']) {
  return useQuery({
    queryKey: keys.milestones(query),
    queryFn: () => unwrap(listMilestones({ query })),
  });
}

// ---------- mutations ----------
//
// Pattern: every mutation hook wraps the generated SDK function with
// `useMutation`, invalidates affected query keys on success, and surfaces
// errors as toasts. Components consume `{ mutate, mutateAsync, isPending }`.

interface MutationApiResult<T> {
  data?: T;
  error?: unknown;
  response?: Response;
}

async function unwrapMutation<T>(promise: Promise<MutationApiResult<T>>): Promise<T> {
  const result = await promise;
  if (result.error !== undefined) throw result.error;
  if (result.data === undefined) {
    // Some endpoints return 204 / no body; treat as success with empty payload.
    return undefined as unknown as T;
  }
  return result.data;
}

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const maybe = err as { title?: string; detail?: string; message?: string };
    return maybe.detail ?? maybe.title ?? maybe.message ?? 'Onbekende fout';
  }
  return 'Onbekende fout';
}

/**
 * Download a Blob as a file in the browser.
 * Used by export endpoints (vCard, CSV) that return file content.
 */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Smoke proof-of-life mutation: download all contacts as a vCard file.
 * Establishes the mutation-hook conventions used by every Phase 7
 * frontend slice (see FRONTEND_WIRING_PLAN.md F0).
 */
export function useExportContactsVCard() {
  return useMutation({
    mutationFn: async () => {
      const result = await exportContactsVCard();
      if (result.error !== undefined) throw result.error;
      if (!result.response) throw new Error('Geen antwoord van server.');
      const blob = await result.response.blob();
      downloadBlob(blob, 'contacten.vcf');
    },
    onSuccess: () => {
      toast.success('vCard-bestand gedownload.');
    },
    onError: (err) => {
      toast.error(`Export mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Create a new contact in the current project.
 * Invalidates every `['contacts', …]` query so the list refetches.
 */
export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateContactCommand) => unwrapMutation(createContact({ body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Contact toegevoegd.');
    },
    onError: (err) => {
      toast.error(`Contact toevoegen mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Update an existing contact. All fields are optional on the wire — pass
 * only the ones that change.
 */
export function useUpdateContact(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateContactCommand) =>
      unwrapMutation(updateContact({ path: { id }, body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Contact bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Contact bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Delete a contact. Caller is responsible for navigating away from any
 * detail screen for the deleted id.
 */
export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(deleteContact({ path: { id } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Contact verwijderd.');
    },
    onError: (err) => {
      toast.error(`Contact verwijderen mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Upload a single document (multipart/form-data). The caller passes the
 * raw File object; phase and name are optional overrides.
 *
 * Returns the SDK's mutation handle. For multi-file flows, drive several
 * `mutateAsync` calls in parallel from the UI; each one tracks its own
 * progress / error.
 */
export interface UploadDocumentArgs {
  file: File;
  phaseId?: string;
  name?: string;
  /** Comma-joined when sent over the wire — the backend splits on `,`. */
  tags?: string[];
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, phaseId, name, tags }: UploadDocumentArgs) =>
      unwrapMutation(
        uploadDocument({
          body: {
            file,
            ...(phaseId ? { phaseId } : {}),
            ...(name ? { name } : {}),
            ...(tags && tags.length > 0 ? { tags: tags.join(',') } : {}),
          },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
    },
    onError: (err) => {
      toast.error(`Upload mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Replace a document's binary with a freshly uploaded file. The server bumps
 * Version, resets ScanStatus → Pending, and re-queues the AV scan. We
 * invalidate the document list AND the per-id blob cache (used by
 * useDocumentBlobUrl in DocPreview) so the embedded preview shows the new
 * content without a hard refresh.
 */
export function useUploadDocumentVersion(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      unwrapMutation(uploadDocumentVersion({ path: { id }, body: { file } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['document-blob', id] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Nieuwe versie geüpload.');
    },
    onError: (err) => {
      toast.error(`Versie uploaden mislukt: ${describeError(err)}`);
    },
  });
}

export function useUpdateDocument(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateDocumentCommand) =>
      unwrapMutation(updateDocument({ path: { id }, body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Document bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Document bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(deleteDocument({ path: { id } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Document verwijderd.');
    },
    onError: (err) => {
      toast.error(`Document verwijderen mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Stream a document from the API and trigger a browser save with the
 * original file name. The hook does not invalidate any query keys —
 * downloads are side-effect-free on server state.
 *
 * The generated SDK's `parseAs: 'auto'` resolves any `application/*` /
 * `image/*` response to `blob`, so we read `result.data` directly. Calling
 * `result.response.blob()` here would fail — the body has already been
 * consumed by the SDK.
 */
export function useDownloadDocument() {
  return useMutation({
    mutationFn: async ({ id, filename }: { id: string; filename: string }) => {
      const result = await downloadDocument({ path: { id } });
      if (result.error !== undefined) throw result.error;
      const blob = result.data;
      if (!(blob instanceof Blob)) throw new Error('Onverwacht antwoordtype van server.');
      downloadBlob(blob, filename);
    },
    onError: (err) => {
      toast.error(`Download mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Bundle a set of documents into a ZIP and trigger a browser save. The
 * backend scopes by project so passing foreign ids returns 404. We coerce
 * whatever the SDK parses into a Blob — same pattern as useExportAccount —
 * and dispatch the download. Filename defaults to the server-provided one
 * (Content-Disposition) but we fall back to a date-stamped name client-side.
 */
export function useDownloadDocumentsZip() {
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (ids.length === 0) throw new Error('Geen documenten geselecteerd.');
      const result = await downloadDocumentsZip({ body: { ids } });
      if (result.error !== undefined) throw result.error;
      const data = result.data;
      const blob =
        data instanceof Blob
          ? data
          : new Blob([typeof data === 'string' ? data : JSON.stringify(data ?? '')], {
              type: 'application/zip',
            });
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `documenten-${stamp}.zip`);
    },
    onSuccess: () => {
      toast.success('ZIP-bestand gedownload.');
    },
    onError: (err) => {
      toast.error(`Download mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Fetch the document's binary via the authenticated SDK and return a blob:
 * URL suitable for inline preview (<img>, <iframe>, etc.). Same lifecycle
 * caveat as usePhotoThumbnailUrl: blob URLs aren't revoked on unmount —
 * TanStack cache shares them across renders, browser reclaims on reload.
 */
export function useDocumentBlobUrl(id: string | undefined) {
  return useQuery({
    queryKey: ['document-blob', id],
    queryFn: async () => {
      if (!id) return null;
      const result = await downloadDocument({ path: { id } });
      if (result.error !== undefined) throw result.error;
      const blob = result.data;
      if (!(blob instanceof Blob)) throw new Error('Onverwacht antwoordtype van server.');
      return URL.createObjectURL(blob);
    },
    staleTime: 5 * 60_000,
    enabled: typeof id === 'string' && id.length > 0,
  });
}

// ---------- photo CRUD ----------

export interface UploadPhotoArgs {
  file: File;
  phaseId?: string;
  room?: string;
  caption?: string;
}

export function useUploadPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, phaseId, room, caption }: UploadPhotoArgs) =>
      unwrapMutation(
        uploadPhoto({
          body: {
            file,
            ...(phaseId ? { phaseId } : {}),
            ...(room ? { room } : {}),
            ...(caption ? { caption } : {}),
          },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photos'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
    },
    onError: (err) => {
      toast.error(`Foto-upload mislukt: ${describeError(err)}`);
    },
  });
}

export function useUpdatePhoto(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePhotoCommand) => unwrapMutation(updatePhoto({ path: { id }, body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photos'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Foto bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Foto bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

export function useDeletePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(deletePhoto({ path: { id } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photos'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Foto verwijderd.');
    },
    onError: (err) => {
      toast.error(`Foto verwijderen mislukt: ${describeError(err)}`);
    },
  });
}

export function useRotatePhoto(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RotatePhotoCommand) => unwrapMutation(rotatePhoto({ path: { id }, body })),
    onSuccess: () => {
      // Photo's Sha256 changed → the photos list refetches (new SHA on the
      // affected row) and the thumbnail blob cache is dropped so the lightbox
      // and tiles pull fresh bytes.
      qc.invalidateQueries({ queryKey: ['photos'] });
      qc.invalidateQueries({ queryKey: ['photo-thumb', id] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Foto gedraaid.');
    },
    onError: (err) => {
      toast.error(`Foto draaien mislukt: ${describeError(err)}`);
    },
  });
}

export function useDownloadPhoto() {
  return useMutation({
    mutationFn: async ({ id, filename }: { id: string; filename: string }) => {
      const result = await downloadPhoto({ path: { id } });
      if (result.error !== undefined) throw result.error;
      const blob = result.data;
      if (!(blob instanceof Blob)) throw new Error('Onverwacht antwoordtype van server.');
      downloadBlob(blob, filename);
    },
    onError: (err) => {
      toast.error(`Download mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Resolve a photo's thumbnail URL by fetching the binary through the
 * authenticated SDK and wrapping it in a blob: URL the browser can render
 * with a plain `<img>`. Used by Photos grid (w=320) and lightbox (w=1600).
 *
 * Blob URLs leak by design — we don't revoke them on unmount because the
 * underlying TanStack cache entry is shared. They get reclaimed on page
 * reload. For a long-running session with many photos, that's an
 * acceptable trade-off; the alternative (manual ref-counting) is much
 * more code for little real-world benefit.
 */
export function usePhotoThumbnailUrl(id: string, width: number) {
  const query = useQuery({
    queryKey: ['photo-thumb', id, width],
    queryFn: async () => {
      const result = await getPhotoThumbnail({ path: { id }, query: { w: width } });
      if (result.error !== undefined) throw result.error;
      const blob = result.data;
      if (!(blob instanceof Blob)) throw new Error('Onverwacht antwoordtype voor thumbnail.');
      return URL.createObjectURL(blob);
    },
    staleTime: 5 * 60_000,
    enabled: id.length > 0,
  });
  return query;
}

// ---------- task CRUD + status / urgent ----------

function invalidateTasksAndKpis(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['tasks'] });
  qc.invalidateQueries({ queryKey: ['activity'] });
  qc.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaskCommand) => unwrapMutation(createTask({ body })),
    onSuccess: () => {
      invalidateTasksAndKpis(qc);
      toast.success('Taak toegevoegd.');
    },
    onError: (err) => {
      toast.error(`Taak toevoegen mislukt: ${describeError(err)}`);
    },
  });
}

export function useUpdateTask(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateTaskCommand) => unwrapMutation(updateTask({ path: { id }, body })),
    onSuccess: () => {
      invalidateTasksAndKpis(qc);
      toast.success('Taak bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Taak bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(deleteTask({ path: { id } })),
    onSuccess: () => {
      invalidateTasksAndKpis(qc);
      toast.success('Taak verwijderd.');
    },
    onError: (err) => {
      toast.error(`Taak verwijderen mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Cycle a task through its lifecycle: Open → InProgress → Done → Open.
 * Blocked is set explicitly via the kebab menu, not the row click.
 */
export function useSetTaskStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: TaskStatus }) =>
      unwrapMutation(setTaskStatus({ path: { id }, body: { status } })),
    onSuccess: () => {
      invalidateTasksAndKpis(qc);
    },
    onError: (err) => {
      toast.error(`Status bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

export function useSetTaskUrgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, urgent }: { id: string; urgent: boolean }) =>
      unwrapMutation(setTaskUrgent({ path: { id }, body: { urgent } })),
    onSuccess: () => {
      invalidateTasksAndKpis(qc);
    },
    onError: (err) => {
      toast.error(`Urgent-vlag mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Batch-create tasks from a werfverslag's action items. Wired by F8; the
 * hook lives here so the SDK surface stays one file.
 */
export function useBatchCreateTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BatchCreateTasksCommand) => unwrapMutation(batchCreateTasks({ body })),
    onSuccess: () => {
      invalidateTasksAndKpis(qc);
      toast.success('Taken aangemaakt.');
    },
    onError: (err) => {
      toast.error(`Batch-aanmaak mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Mail a werfverslag to its attendee contacts. The toast reports how many
 * attendees received it and how many were skipped (no email on file).
 */
export function useSendWerfverslag() {
  return useMutation({
    mutationFn: (id: string) =>
      unwrapMutation<SendWerfverslagResult>(sendWerfverslag({ path: { id } })),
    onSuccess: (result) => {
      const sent = Number(result.sent);
      const skipped = Number(result.skipped);
      if (sent === 0) {
        toast.error('Geen aanwezigen met een e-mailadres om naar te versturen.');
        return;
      }
      const skippedNote = skipped > 0 ? ` (${skipped} zonder e-mail overgeslagen)` : '';
      const plural = sent === 1 ? 'aanwezige' : 'aanwezigen';
      toast.success(`Verslag verstuurd naar ${sent} ${plural}${skippedNote}.`);
    },
    onError: (err) => {
      toast.error(`Verslag versturen mislukt: ${describeError(err)}`);
    },
  });
}

// ---------- decision CRUD + choose ----------

function invalidateDecisionsAndKpis(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['decisions'] });
  qc.invalidateQueries({ queryKey: ['activity'] });
  qc.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useCreateDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDecisionCommand) => unwrapMutation(createDecision({ body })),
    onSuccess: () => {
      invalidateDecisionsAndKpis(qc);
      toast.success('Beslissing toegevoegd.');
    },
    onError: (err) => {
      toast.error(`Beslissing toevoegen mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Lock in an option for an open decision. The card flips from "Wacht op
 * jouw beslissing" into "Vastgelegd" once the server returns; we drive
 * that purely through query invalidation, not optimistic state.
 */
export function useChooseDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, chosen }: { id: string; chosen: string }) =>
      unwrapMutation(chooseDecision({ path: { id }, body: { chosen } })),
    onSuccess: () => {
      invalidateDecisionsAndKpis(qc);
      toast.success('Keuze vastgelegd.');
    },
    onError: (err) => {
      toast.error(`Keuze vastleggen mislukt: ${describeError(err)}`);
    },
  });
}

export function useUpdateDecision(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateDecisionCommand) =>
      unwrapMutation(updateDecision({ path: { id }, body })),
    onSuccess: () => {
      invalidateDecisionsAndKpis(qc);
      toast.success('Beslissing bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Beslissing bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

export function useDeleteDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(deleteDecision({ path: { id } })),
    onSuccess: () => {
      invalidateDecisionsAndKpis(qc);
      toast.success('Beslissing verwijderd.');
    },
    onError: (err) => {
      toast.error(`Beslissing verwijderen mislukt: ${describeError(err)}`);
    },
  });
}

// ---------- meerwerk CRUD + approve/reject + export ----------

function invalidateMeerwerkenAndBudget(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['meerwerken'] });
  qc.invalidateQueries({ queryKey: ['budget'] });
  qc.invalidateQueries({ queryKey: ['activity'] });
  qc.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useCreateMeerwerk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMeerwerkCommand) => unwrapMutation(createMeerwerk({ body })),
    onSuccess: () => {
      invalidateMeerwerkenAndBudget(qc);
      toast.success('Meerwerk voorgesteld.');
    },
    onError: (err) => {
      toast.error(`Meerwerk toevoegen mislukt: ${describeError(err)}`);
    },
  });
}

export function useUpdateMeerwerk(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMeerwerkCommand) =>
      unwrapMutation(updateMeerwerk({ path: { id }, body })),
    onSuccess: () => {
      invalidateMeerwerkenAndBudget(qc);
      toast.success('Meerwerk bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Meerwerk bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Approve a "Voorgesteld" meerwerk. The budget endpoint counts approved
 * meerwerken into the pledged-pct bar, so we invalidate `['budget']`
 * alongside the meerwerken list to keep the dashboard ribbons honest.
 */
export function useApproveMeerwerk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(approveMeerwerk({ path: { id } })),
    onSuccess: () => {
      invalidateMeerwerkenAndBudget(qc);
      toast.success('Meerwerk goedgekeurd.');
    },
    onError: (err) => {
      toast.error(`Goedkeuring mislukt: ${describeError(err)}`);
    },
  });
}

export function useRejectMeerwerk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(rejectMeerwerk({ path: { id } })),
    onSuccess: () => {
      invalidateMeerwerkenAndBudget(qc);
      toast.success('Meerwerk afgewezen.');
    },
    onError: (err) => {
      toast.error(`Afwijzen mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Export the meerwerken register as CSV. SDK auto-parses `text/csv`
 * responses as text (see getParseAs), so we wrap the data back in a
 * Blob for the browser save handler.
 */
export function useExportMeerwerkenCsv() {
  return useMutation({
    mutationFn: async () => {
      const result = await exportMeerwerkenCsv();
      if (result.error !== undefined) throw result.error;
      const csv = result.data;
      const blob =
        csv instanceof Blob
          ? csv
          : new Blob([typeof csv === 'string' ? csv : ''], { type: 'text/csv;charset=utf-8' });
      downloadBlob(blob, 'meerwerken.csv');
    },
    onSuccess: () => {
      toast.success('CSV-bestand gedownload.');
    },
    onError: (err) => {
      toast.error(`Export mislukt: ${describeError(err)}`);
    },
  });
}

// ---------- invoice CRUD + workflow + budget export ----------

function invalidateInvoicesAndBudget(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['invoices'] });
  qc.invalidateQueries({ queryKey: ['budget'] });
  qc.invalidateQueries({ queryKey: ['activity'] });
  qc.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInvoiceCommand) => unwrapMutation(createInvoice({ body })),
    onSuccess: () => {
      invalidateInvoicesAndBudget(qc);
      toast.success('Factuur toegevoegd.');
    },
    onError: (err) => {
      toast.error(`Factuur toevoegen mislukt: ${describeError(err)}`);
    },
  });
}

export function useUpdateInvoice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateInvoiceCommand) =>
      unwrapMutation(updateInvoice({ path: { id }, body })),
    onSuccess: () => {
      invalidateInvoicesAndBudget(qc);
      toast.success('Factuur bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Factuur bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(deleteInvoice({ path: { id } })),
    onSuccess: () => {
      invalidateInvoicesAndBudget(qc);
      toast.success('Factuur verwijderd.');
    },
    onError: (err) => {
      toast.error(`Factuur verwijderen mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Approve an invoice — flips status to Paid on the backend and increases
 * the budget's "spent" total. Hits the same invalidation set as the
 * other invoice mutations so the dashboard ribbons stay honest.
 */
export function useApproveInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(approveInvoice({ path: { id } })),
    onSuccess: () => {
      invalidateInvoicesAndBudget(qc);
      toast.success('Factuur goedgekeurd.');
    },
    onError: (err) => {
      toast.error(`Goedkeuring mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * "Vraag toelichting" — backend records the action and sends a mail to
 * the supplier. BF-3 added an optional free-text `note` that lands in
 * the activity payload; pass `note: null` (or omit) for the legacy
 * "no note" flow.
 */
export function useClarifyInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; note?: string | null }) =>
      unwrapMutation(clarifyInvoice({ path: { id: args.id }, body: { note: args.note ?? null } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Toelichting aangevraagd.');
    },
    onError: (err) => {
      toast.error(`Toelichting aanvragen mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * "Bezwaar registreren" — backend records the dispute. BF-3 makes the
 * `reason` field required so the audit trail captures why the bouwheer
 * pushed back. Callers must pass a non-empty reason.
 */
export function useObjectInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      unwrapMutation(objectInvoice({ path: { id: args.id }, body: { reason: args.reason } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Bezwaar geregistreerd.');
    },
    onError: (err) => {
      toast.error(`Bezwaar mislukt: ${describeError(err)}`);
    },
  });
}

export function useExportBudgetCsv() {
  return useMutation({
    mutationFn: async () => {
      const result = await exportBudgetCsv();
      if (result.error !== undefined) throw result.error;
      const csv = result.data;
      const blob =
        csv instanceof Blob
          ? csv
          : new Blob([typeof csv === 'string' ? csv : ''], { type: 'text/csv;charset=utf-8' });
      downloadBlob(blob, 'budget.csv');
    },
    onSuccess: () => {
      toast.success('CSV-bestand gedownload.');
    },
    onError: (err) => {
      toast.error(`Export mislukt: ${describeError(err)}`);
    },
  });
}

// ---------- quote CRUD + select ----------

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateQuoteCommand) => unwrapMutation(createQuote({ body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Offerte toegevoegd.');
    },
    onError: (err) => {
      toast.error(`Offerte toevoegen mislukt: ${describeError(err)}`);
    },
  });
}

export function useUpdateQuote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateQuoteCommand) => unwrapMutation(updateQuote({ path: { id }, body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Offerte bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Offerte bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(deleteQuote({ path: { id } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Offerte verwijderd.');
    },
    onError: (err) => {
      toast.error(`Offerte verwijderen mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Mark a quote as the project's preferred one. Mutually exclusive
 * within a subject — backend clears `preferred` on other quotes with
 * the same subject; we just invalidate so the UI re-reads.
 */
export function useSelectQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(selectQuote({ path: { id } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Offerte geselecteerd.');
    },
    onError: (err) => {
      toast.error(`Selecteren mislukt: ${describeError(err)}`);
    },
  });
}

// ---------- plan CRUD (metadata-only in v1) ----------

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePlanCommand) => unwrapMutation(createPlan({ body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plans'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Plan toegevoegd.');
    },
    onError: (err) => {
      toast.error(`Plan toevoegen mislukt: ${describeError(err)}`);
    },
  });
}

export function useUpdatePlan(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePlanCommand) => unwrapMutation(updatePlan({ path: { id }, body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plans'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Plan bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Plan bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrapMutation(deletePlan({ path: { id } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plans'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Plan verwijderd.');
    },
    onError: (err) => {
      toast.error(`Plan verwijderen mislukt: ${describeError(err)}`);
    },
  });
}

// BF-8 — Plans now support a multipart upload (BF-1 backend landed
// `POST /api/plans/upload`). The metadata-only `useCreatePlan` above
// stays so users can register a plan record without a file (e.g. an
// architect's draft tracked by version only).
export interface UploadPlanArgs {
  file: File;
  name: string;
  discipline: string;
  version: string;
  approved?: boolean;
  active?: boolean;
}

export function useUploadPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, name, discipline, version, approved, active }: UploadPlanArgs) =>
      unwrapMutation(
        uploadPlan({
          body: {
            file,
            name,
            discipline,
            version,
            ...(approved !== undefined ? { approved } : {}),
            ...(active !== undefined ? { active } : {}),
          },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plans'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Plan-PDF geüpload.');
    },
    onError: (err) => {
      toast.error(`Plan-PDF upload mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Replace the underlying PDF for an existing plan. Mirrors
 * useUploadDocumentVersion: server rewrites sha256/size/mime/fileName plus
 * uploadedAt/uploadedBy, but does NOT touch the user-controlled
 * Plan.Version string. Invalidates the plan-file blob cache so the viewer
 * re-renders the new PDF without a hard refresh.
 */
export function useUploadPlanVersion(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => unwrapMutation(uploadPlanVersion({ path: { id }, body: { file } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plans'] });
      qc.invalidateQueries({ queryKey: ['plan-file', id] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Nieuwe versie geüpload.');
    },
    onError: (err) => {
      toast.error(`Versie uploaden mislukt: ${describeError(err)}`);
    },
  });
}

// BF-8 — Stream the underlying PDF for a plan. Mirrors useDownloadDocument's
// pattern: SDK parses the blob, we trigger a save with the plan's filename.
export function useDownloadPlan() {
  return useMutation({
    mutationFn: async ({ id, filename }: { id: string; filename: string }) => {
      const result = await downloadPlan({ path: { id } });
      if (result.error !== undefined) throw result.error;
      const blob = result.data;
      if (!(blob instanceof Blob)) throw new Error('Onverwacht antwoordtype van server.');
      downloadBlob(blob, filename);
    },
    onError: (err) => {
      toast.error(`Plan-download mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * BF-8 viewer support — turn the authenticated /api/plans/{id}/download
 * stream into a blob: URL that `<embed>` / `<img>` can render directly.
 * Same trade-offs as usePhotoThumbnailUrl: we don't revoke on unmount.
 */
export function usePlanFileUrl(id: string | undefined) {
  return useQuery({
    queryKey: ['plan-file', id ?? ''],
    queryFn: async () => {
      if (!id) return '';
      const result = await downloadPlan({ path: { id } });
      if (result.error !== undefined) throw result.error;
      const blob = result.data;
      if (!(blob instanceof Blob)) throw new Error('Onverwacht antwoordtype voor plan.');
      return URL.createObjectURL(blob);
    },
    staleTime: 5 * 60_000,
    enabled: Boolean(id),
  });
}

// ---------- keuring create / update + attest upload ----------

export function useCreateKeuring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateKeuringCommand) => unwrapMutation(createKeuring({ body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keuringen'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Keuring ingepland.');
    },
    onError: (err) => {
      toast.error(`Keuring inplannen mislukt: ${describeError(err)}`);
    },
  });
}

export function useUpdateKeuring(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateKeuringCommand) =>
      unwrapMutation(updateKeuring({ path: { id }, body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keuringen'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Keuring bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Keuring bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

export function useSeedStandardKeuringen(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrapMutation(seedStandardKeuringen({ path: { projectId } })),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['keuringen'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      const created = result.created.length;
      const skipped = result.skipped.length;
      const skipText = skipped > 0 ? ` · ${skipped} overgeslagen (al aanwezig)` : '';
      toast.success(`${created} keuringen toegevoegd${skipText}`);
    },
    onError: (err) => {
      toast.error(`Standaardlijst toevoegen mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Upload an attest PDF for a keuring (multipart). On success the
 * keuring transitions to `Done` server-side.
 */
export function useAttestKeuring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      unwrapMutation(attestKeuring({ path: { id }, body: { file } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keuringen'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Attest geüpload.');
    },
    onError: (err) => {
      toast.error(`Attest uploaden mislukt: ${describeError(err)}`);
    },
  });
}

// ---------- werfverslag create / update ----------

export function useCreateWerfverslag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWerfverslagCommand) => unwrapMutation(createWerfverslag({ body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['werfverslagen'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Werfverslag aangemaakt.');
    },
    onError: (err) => {
      toast.error(`Werfverslag aanmaken mislukt: ${describeError(err)}`);
    },
  });
}

export function useUpdateWerfverslag(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateWerfverslagCommand) =>
      unwrapMutation(updateWerfverslag({ path: { id }, body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['werfverslagen'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Werfverslag bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Werfverslag bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

// ---------- phase update + milestone create + mail responsibles ----------

function invalidatePhaseAndKpis(qc: ReturnType<typeof useQueryClient>, phaseId?: string) {
  qc.invalidateQueries({ queryKey: ['phases'] });
  if (phaseId) {
    qc.invalidateQueries({ queryKey: ['phases', phaseId] });
  }
  qc.invalidateQueries({ queryKey: ['dashboard'] });
  qc.invalidateQueries({ queryKey: ['activity'] });
}

export function useUpdatePhase(phaseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePhaseCommand) =>
      unwrapMutation(updatePhase({ path: { phaseId }, body })),
    onSuccess: () => {
      invalidatePhaseAndKpis(qc, phaseId);
      toast.success('Fase bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Fase bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Send a notification mail to every contact assigned to this phase.
 * The backend handles recipient resolution and Resend dispatch; the
 * frontend only fires-and-forgets and reflects success in a toast.
 */
export function useMailPhaseContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (phaseId: string) => unwrapMutation(mailPhaseContacts({ path: { phaseId } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Verantwoordelijken gemaild.');
    },
    onError: (err) => {
      toast.error(`Mail versturen mislukt: ${describeError(err)}`);
    },
  });
}

export function useCreateMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMilestoneCommand) => unwrapMutation(createMilestone({ body })),
    onSuccess: () => {
      // BF-10 — invalidate the read so the just-created milestone shows up
      // on the Mijlpalen strip without a manual refresh.
      qc.invalidateQueries({ queryKey: ['milestones'] });
      qc.invalidateQueries({ queryKey: ['phases'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      toast.success('Mijlpaal toegevoegd.');
    },
    onError: (err) => {
      toast.error(`Mijlpaal toevoegen mislukt: ${describeError(err)}`);
    },
  });
}

// ---------- project update ----------

/**
 * Apply the onboarding wizard's collected answers. BF-11 added a real
 * `GET /api/project` so we invalidate that alongside the dashboard.
 *
 * 2026-05-16 race fix — onSuccess is `async` and AWAITS the invalidations
 * (and the implicit refetches they trigger for active observers) before
 * mutateAsync resolves. The onboarding wizard navigates to `/` immediately
 * after the mutation, and the NeedsOnboardingGate reads the dashboard's
 * projectName. Without the await, the navigate would land before the
 * refetch completed and the gate would read the stale empty name and bounce
 * the user back to /onboarding. We also patch the cached dashboard +
 * project payloads with the just-submitted values so the gate sees fresh
 * data even when there's no active observer to trigger a refetch.
 */
export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProjectCommand) => unwrapMutation(updateProject({ body })),
    onSuccess: async (_data, body) => {
      // Belt-and-braces: patch the dashboard cache directly with the new
      // name/address. This means the gate sees fresh data even when no
      // observer was active at submit time (network refetch may race the
      // navigate; this avoids relying on refetch ordering).
      if (body.name !== null && body.name !== undefined) {
        qc.setQueryData(['dashboard'], (old: unknown) => {
          if (!old || typeof old !== 'object') return old;
          return { ...old, projectName: body.name };
        });
      }
      if (body.address !== null && body.address !== undefined) {
        qc.setQueryData(['dashboard'], (old: unknown) => {
          if (!old || typeof old !== 'object') return old;
          return { ...old, projectAddress: body.address };
        });
      }
      // Awaited: invalidateQueries' promise resolves after observer refetches
      // settle. With the cache already patched above, this is a defensive
      // refresh so the dashboard / project / activity tabs see the
      // canonical server-side values (incl. the server-derived currentPhase).
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.project() }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['phases'] }),
        qc.invalidateQueries({ queryKey: ['activity'] }),
      ]);
      toast.success('Project bijgewerkt.');
    },
    onError: (err) => {
      toast.error(`Project bijwerken mislukt: ${describeError(err)}`);
    },
  });
}

// ---------- F12: settings ----------

/**
 * Toggle a notification preference. v1 has no `getNotifications` read
 * endpoint — the page tracks toggle state locally and posts the full
 * settings array on every change. Debounced at the call site (Settings.tsx)
 * so a rapid sequence of toggles only fires one request.
 */
export function useUpdateNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateNotificationsCommand) => unwrapMutation(updateNotifications({ body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.notifications() });
      qc.invalidateQueries({ queryKey: ['activity'] });
    },
    onError: (err) => {
      toast.error(`Notificatie-instelling bewaren mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Invite a teammate by email. Backend produces the magic-link and dispatches
 * a Resend mail; we surface a success toast — no read endpoint exists yet to
 * reflect the pending invite back into the UI.
 */
export function useInviteUser() {
  return useMutation({
    mutationFn: (body: InviteUserCommand) => unwrapMutation(inviteUser({ body })),
    onSuccess: () => {
      toast.success('Uitnodiging verstuurd.');
    },
    onError: (err) => {
      toast.error(`Uitnodigen mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Create a read-only share link. The 201 response body shape isn't strongly
 * typed in the SDK — we accept whatever comes back and let the caller cope.
 * The caller renders the link with a copy-to-clipboard control.
 */
export function useCreateShareLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrapMutation(createShareLink()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.shareLinks() });
      toast.success('Deellink aangemaakt.');
    },
    onError: (err) => {
      toast.error(`Deellink aanmaken mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Upsert the per-post expected costs for the current project. Invalidates the
 * estimates query (and budget, in case a derived view depends on it).
 */
export function useSetBudgetEstimates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetBudgetEstimatesCommand) => unwrapMutation(setBudgetEstimates({ body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['budget'] });
      toast.success('Verwachte kosten opgeslagen.');
    },
    onError: (err) => {
      toast.error(`Opslaan mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Trigger a ZIP download of the user's data (GDPR Article 20 portability).
 * Backend streams the ZIP; we coerce whatever the SDK parses into a Blob
 * — same pattern as the meerwerken / budget CSV exports — and dispatch
 * a download. F14 surface: caller wraps this in a confirm dialog.
 */
export function useExportAccount() {
  return useMutation({
    mutationFn: async () => {
      const result = await exportAccount();
      if (result.error !== undefined) throw result.error;
      const data = result.data;
      const blob =
        data instanceof Blob
          ? data
          : new Blob([typeof data === 'string' ? data : JSON.stringify(data ?? '')], {
              type: 'application/zip',
            });
      return blob;
    },
    onSuccess: (blob) => {
      downloadBlob(blob, 'bouw-export.zip');
      toast.success('Export gedownload.');
    },
    onError: (err) => {
      toast.error(`Export mislukt: ${describeError(err)}`);
    },
  });
}

/**
 * Permanently delete the signed-in user's account (GDPR Article 17 erasure).
 * Caller is responsible for sign-out + redirect on success — F14 wraps this
 * in a type-`VERWIJDER` confirm and a Clerk signOut() → /sign-in redirect.
 */
export function useDeleteAccount() {
  return useMutation({
    mutationFn: () => unwrapMutation(deleteAccount()),
    onError: (err) => {
      toast.error(`Account verwijderen mislukt: ${describeError(err)}`);
    },
  });
}

// ---------- F13: search + activity export ----------

/**
 * Full-text search across the project's entities. Disabled when the query
 * string is shorter than the minimum (caller passes `enabled`) so each
 * keystroke under the threshold doesn't burn a request.
 */
export function useSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: ['search', query],
    queryFn: () => unwrap(search({ query: { q: query } })),
    enabled: enabled && query.trim().length >= 2,
    staleTime: 30_000,
  });
}

export type { SearchResultItem };

/**
 * Download the activity log as CSV. The SDK exposes /api/activity/export as
 * `unknown`; we treat the response as a text/csv body and stream it to a
 * Blob → object-URL → anchor click for the same pattern as the
 * meerwerken / budget CSV exports.
 */
export function useExportActivityCsv() {
  return useMutation({
    mutationFn: async () => {
      const text = (await unwrapMutation(exportActivityCsv())) as unknown as string;
      return new Blob([typeof text === 'string' ? text : JSON.stringify(text)], {
        type: 'text/csv;charset=utf-8',
      });
    },
    onSuccess: (blob) => {
      downloadBlob(blob, 'activiteit.csv');
      toast.success('Activiteit geëxporteerd.');
    },
    onError: (err) => {
      toast.error(`Export mislukt: ${describeError(err)}`);
    },
  });
}

// Re-export shared helpers so future slices can use them without
// reimplementing.
export { describeError, downloadBlob, unwrapMutation, useMutation, useQueryClient };
